/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { AutocompleteItemDto, AutocompleteItemMapper, ID } from '@gh/common-shared-data';
import { AbstractRestService, arrayOf, identity, none, RestSettings, SlicedDataSource } from '@gh/core-data';
import { Observable } from 'rxjs';
import { map$ } from '@gh/rx/operators';
import {
  JobDppCreateRequestDto,
  SalesFolderCompleteDto,
  SalesFolderDto,
  SalesFolderRfqGeneralDto,
  SalesFolderShortDto,
} from './sales-folder.dto';
import {
  JobDppCreateRequestMapper,
  SalesFolderCompleteMapper,
  SalesFolderRfqGeneralMapper,
  SalesFolderShortMapper,
} from './sales-folder.mapper';
import { UTCToLocal } from '@gh/core-util';

@Injectable()
export class SalesFolderRestService extends AbstractRestService {
  constructor(settings: RestSettings) {
    super(settings);
  }

  createDppJob(sfId: ID, details: JobDppCreateRequestDto): Observable<void> {
    return this.httpPost(`${this.bp}/leads/sales-folders/${sfId}/dpp-job`, details, JobDppCreateRequestMapper, none());
  }

  getRfqGeneral(sfId: ID): Observable<SalesFolderRfqGeneralDto> {
    return this.httpGet(`${this.crm}/leads/sales-folders/${sfId}/rfq-general`, SalesFolderRfqGeneralMapper);
  }

  updateRfqGeneral(rfq: SalesFolderRfqGeneralDto): Observable<SalesFolderRfqGeneralDto> {
    return this.httpPut(
      `${this.bp}/leads/sales-folders/${rfq.salesFolderId}/rfq-general`,
      rfq,
      SalesFolderRfqGeneralMapper);
  }

  get(sfId: ID): Observable<SalesFolderDto> {
    return this.httpGet(`${this.crm}/leads/sales-folders/${sfId}`, SalesFolderCompleteMapper)
      .pipe(map$((res) => ({
        ...res,
        followUp: res.followUp ? UTCToLocal(res.followUp) : res.followUp,
        notProceedingReason: res.notProceedingReason ? `${res.notProceedingReason}` : '',
      })));
  }

  findAllByLeadId(leadId: ID): Observable<SalesFolderShortDto[]> {
    return this.httpGet(`${this.crm}/leads/${leadId}/sales-folders`, arrayOf(SalesFolderShortMapper));
  }

  findAllActiveByLeadId(leadId: ID): Observable<SalesFolderShortDto[]> {
    return this.httpGet(`${this.crm}/leads/${leadId}/sales-folders/active`, arrayOf(SalesFolderShortMapper));
  }

  complete(sfId: ID, dto: SalesFolderCompleteDto): Observable<void> {
    return this.httpPost(`${this.bp}/leads/sales-folders/${sfId}/complete`, dto, identity(), none());
  }

  findAllAsAutocompleteDataSource(searchString?: string): SlicedDataSource<AutocompleteItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.crm}/sales-folders`,
      { searchString },
      identity(),
      arrayOf(AutocompleteItemMapper));
  }
}
